import { useState, useMemo } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, DollarSign, Calendar, Car, Check, TrendingDown, TrendingUp, BarChart3, X, MoreHorizontal, Trash2 } from "lucide-react";
import { useTrips } from "@/hooks/useTrips";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { EarningsImportDialog } from "@/components/earnings/EarningsImportDialog";
import { getTripInstallments } from "@/lib/tripPayoutSchedule";
import { ClearTripsDialog } from "@/components/earnings/ClearTripsDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import analytics from "@/lib/segment";

const Earnings = () => {
  const { trips, isLoading, clearAllTrips } = useTrips();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [highlight, setHighlight] = useState<{ type: 'vehicle' | 'trip' | 'month'; value: string } | null>(null);

  const handleHighlight = (type: 'vehicle' | 'trip' | 'month', value: string) => {
    setHighlight((prev) => (prev?.type === type && prev?.value === value ? null : { type, value }));
  };

  const isTripHighlighted = (trip: NonNullable<typeof trips>[number]) => {
    if (!highlight) return false;
    if (highlight.type === 'vehicle') {
      const tripKey = trip.vehicle_id || trip.turo_vehicle_name || "unknown";
      return tripKey === highlight.value;
    }
    if (highlight.type === 'trip') {
      return trip.id === highlight.value;
    }
    if (highlight.type === 'month') {
      if (!trip.trip_start) return false;
      try {
        return format(parseISO(trip.trip_start), "MMM yyyy") === highlight.value;
      } catch {
        return false;
      }
    }
    return false;
  };

  const hasTrips = (trips?.length ?? 0) > 0;

  const formatCurrency = (value: number | null | undefined) => {
    if (value == null) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "MMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  // Calculate summary stats
  const totalEarnings = trips?.reduce((sum, t) => sum + (t.total_earnings || 0), 0) || 0;
  const completedTrips = trips?.filter(t => t.trip_status?.toLowerCase() === "completed").length || 0;
  const cancelledTrips = trips?.filter(t => t.trip_status?.toLowerCase().includes("cancel")).length || 0;

  // Calculate daily earnings metrics (excluding cancelled trips)
  const nonCancelledTrips = trips?.filter(t => 
    !t.trip_status?.toLowerCase().includes("cancel") && 
    t.total_earnings != null && 
    t.trip_days != null && 
    t.trip_days > 0
  ) || [];

  const dailyEarningsPerTrip = nonCancelledTrips.map(t => ({
    trip: t,
    earnings: t.total_earnings!,
    days: t.trip_days!,
    dailyAvg: t.total_earnings! / t.trip_days!
  }));

  const lowestEarningTrip = dailyEarningsPerTrip.length > 0
    ? dailyEarningsPerTrip.reduce((min, t) => t.dailyAvg < min.dailyAvg ? t : min)
    : null;

  const highestEarningTrip = dailyEarningsPerTrip.length > 0
    ? dailyEarningsPerTrip.reduce((max, t) => t.dailyAvg > max.dailyAvg ? t : max)
    : null;

  const totalDays = nonCancelledTrips.reduce((sum, t) => sum + (t.trip_days || 0), 0);
  const totalNonCancelledEarnings = nonCancelledTrips.reduce((sum, t) => sum + (t.total_earnings || 0), 0);
  const averageDailyEarning = totalDays > 0 ? totalNonCancelledEarnings / totalDays : null;

  // Calculate monthly earnings for bar chart (Turo-style installments)
  const monthlyEarnings = useMemo(() => {
    if (!trips || trips.length === 0) return [];

    const byMonth: Record<string, { settled: number; upcoming: number }> = {};

    trips.forEach(trip => {
      const installments = getTripInstallments(trip);
      installments.forEach(inst => {
        const monthKey = format(inst.date, "yyyy-MM");
        if (!byMonth[monthKey]) byMonth[monthKey] = { settled: 0, upcoming: 0 };
        if (inst.settled) byMonth[monthKey].settled += inst.amount;
        else byMonth[monthKey].upcoming += inst.amount;
      });
    });

    return Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, v]) => ({
        month: format(parseISO(month + "-01"), "MMM yyyy"),
        settled: Math.round(v.settled * 100) / 100,
        upcoming: Math.round(v.upcoming * 100) / 100,
      }));
  }, [trips]);

  // Calculate earnings per vehicle
  const vehicleEarnings = useMemo(() => {
    if (!trips || trips.length === 0) return [];

    const byVehicle: Record<string, { key: string; name: string; imageUrl: string | null; earnings: number; tripCount: number; days: number; miles: number }> = {};

    trips.forEach(trip => {
      if (trip.trip_status?.toLowerCase().includes("cancel")) return;
      const vehicleKey = trip.vehicle_id || trip.turo_vehicle_name || "unknown";
      const vehicleName = trip.vehicles?.name || trip.turo_vehicle_name || "Unassigned";

      if (!byVehicle[vehicleKey]) {
        byVehicle[vehicleKey] = { key: vehicleKey, name: vehicleName, imageUrl: trip.vehicles?.image_url || null, earnings: 0, tripCount: 0, days: 0, miles: 0 };
      }
      byVehicle[vehicleKey].earnings += trip.total_earnings || 0;
      byVehicle[vehicleKey].tripCount += 1;
      byVehicle[vehicleKey].days += trip.trip_days || 0;
      byVehicle[vehicleKey].miles += trip.distance_traveled || 0;
    });

    return Object.values(byVehicle).sort((a, b) => b.earnings - a.earnings);
  }, [trips]);

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Earnings</h1>
            <p className="text-muted-foreground mt-1">
              Import and track your Turo trip earnings
            </p>
          </div>
          <Button onClick={() => setImportDialogOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10">
                  <DollarSign className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Earnings</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalEarnings)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-purple-500/10">
                  <BarChart3 className="h-6 w-6 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Average Earnings Per Day</p>
                  <p className="text-2xl font-bold">
                    {averageDailyEarning != null ? formatCurrency(averageDailyEarning) : "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-green-500/10">
                  <Calendar className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Trips</p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <Check className="h-4 w-4 text-green-500" />
                      <span className="text-2xl font-bold">{completedTrips}</span>
                    </div>
                    <span className="text-muted-foreground">/</span>
                    <div className="flex items-center gap-1.5">
                      <X className="h-4 w-4 text-destructive" />
                      <span className="text-2xl font-bold text-muted-foreground">{cancelledTrips}</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Completed / Cancelled</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Daily Earnings Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card
            className={`cursor-pointer transition-all ${
              highlight?.type === 'trip' && highlight?.value === lowestEarningTrip?.trip.id
                ? "ring-2 ring-primary bg-primary/5"
                : "hover:shadow-md"
            }`}
            onClick={() => lowestEarningTrip && handleHighlight('trip', lowestEarningTrip.trip.id)}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-orange-500/10">
                  <TrendingDown className="h-6 w-6 text-orange-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-muted-foreground">Lowest-Earning Day</p>
                  <p className="text-2xl font-bold">
                    {lowestEarningTrip != null ? formatCurrency(lowestEarningTrip.dailyAvg) : "-"}
                  </p>
                  {lowestEarningTrip && (
                    <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                      <p className="truncate" title={lowestEarningTrip.trip.vehicles?.name || lowestEarningTrip.trip.turo_vehicle_name || undefined}>
                        {lowestEarningTrip.trip.vehicles?.name || lowestEarningTrip.trip.turo_vehicle_name || "Unknown vehicle"}
                      </p>
                      <p>Guest: {lowestEarningTrip.trip.guest_name || "Unknown"}</p>
                      <p>
                        {formatDate(lowestEarningTrip.trip.trip_start)} – {formatDate(lowestEarningTrip.trip.trip_end)} ({lowestEarningTrip.days} days)
                      </p>
                      <p className="font-medium">Total: {formatCurrency(lowestEarningTrip.earnings)}</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all ${
              highlight?.type === 'trip' && highlight?.value === highestEarningTrip?.trip.id
                ? "ring-2 ring-primary bg-primary/5"
                : "hover:shadow-md"
            }`}
            onClick={() => highestEarningTrip && handleHighlight('trip', highestEarningTrip.trip.id)}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-blue-500/10">
                  <TrendingUp className="h-6 w-6 text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-muted-foreground">Highest-Earning Day</p>
                  <p className="text-2xl font-bold">
                    {highestEarningTrip != null ? formatCurrency(highestEarningTrip.dailyAvg) : "-"}
                  </p>
                  {highestEarningTrip && (
                    <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                      <p className="truncate" title={highestEarningTrip.trip.vehicles?.name || highestEarningTrip.trip.turo_vehicle_name || undefined}>
                        {highestEarningTrip.trip.vehicles?.name || highestEarningTrip.trip.turo_vehicle_name || "Unknown vehicle"}
                      </p>
                      <p>Guest: {highestEarningTrip.trip.guest_name || "Unknown"}</p>
                      <p>
                        {formatDate(highestEarningTrip.trip.trip_start)} – {formatDate(highestEarningTrip.trip.trip_end)} ({highestEarningTrip.days} days)
                      </p>
                      <p className="font-medium">Total: {formatCurrency(highestEarningTrip.earnings)}</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-muted-foreground">Earnings by Month</p>
                {monthlyEarnings.length > 0 && (
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-sm" style={{ background: "hsl(var(--primary))" }} />
                      Settled
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-sm" style={{ background: "hsl(var(--primary) / 0.35)" }} />
                      Upcoming
                    </div>
                  </div>
                )}
              </div>
              {monthlyEarnings.length > 0 ? (
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={monthlyEarnings}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="month"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "var(--radius)",
                        color: "hsl(var(--popover-foreground))",
                      }}
                      formatter={(value: number, name: string) => [
                        formatCurrency(value),
                        name === "settled" ? "Settled" : "Upcoming",
                      ]}
                    />
                    <Bar
                      dataKey="settled"
                      stackId="earnings"
                      fill="hsl(var(--primary))"
                      cursor="pointer"
                      onClick={(data: { month?: string }) => {
                        if (data?.month) handleHighlight('month', data.month);
                      }}
                    />
                    <Bar
                      dataKey="upcoming"
                      stackId="earnings"
                      fill="hsl(var(--primary) / 0.35)"
                      radius={[4, 4, 0, 0]}
                      cursor="pointer"
                      onClick={(data: { month?: string }) => {
                        if (data?.month) handleHighlight('month', data.month);
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[120px] flex items-center justify-center text-muted-foreground text-sm">
                  No data yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Vehicle Earnings Breakdown */}
        {vehicleEarnings.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Car className="h-5 w-5 text-primary" />
              Earnings by Vehicle
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicleEarnings.map((v, idx) => {
                const dailyAvg = v.days > 0 ? v.earnings / v.days : 0;
                const perMile = v.miles > 0 ? v.earnings / v.miles : null;
                return (
                  <Card
                    key={idx}
                    className={`cursor-pointer transition-all ${
                      highlight?.type === 'vehicle' && highlight?.value === v.key
                        ? "ring-2 ring-primary bg-primary/5"
                        : "hover:shadow-md"
                    }`}
                    onClick={() => handleHighlight('vehicle', v.key)}
                  >
                    <CardContent className="pt-6">
                      <div className="flex gap-4">
                        <Avatar className="h-16 w-16 shrink-0 rounded-lg">
                          {v.imageUrl && <AvatarImage src={v.imageUrl} alt={v.name} className="rounded-lg object-cover" />}
                          <AvatarFallback className="rounded-lg">
                            <Car className="h-6 w-6" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate" title={v.name}>
                                {v.name}
                              </p>
                              <p className="text-2xl font-bold mt-0.5">{formatCurrency(v.earnings)}</p>
                            </div>
                            <Badge variant="secondary" className="text-xs shrink-0">
                              {v.tripCount} trip{v.tripCount !== 1 ? "s" : ""}
                            </Badge>
                          </div>
                          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                            <span>{v.days} day{v.days !== 1 ? "s" : ""} rented</span>
                            <span>•</span>
                            <span>{formatCurrency(dailyAvg)}/day</span>
                            {perMile != null && (
                              <>
                                <span>•</span>
                                <span>{formatCurrency(perMile)}/mile</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Trips Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Car className="h-5 w-5" />
                Trip History
              </CardTitle>
              <CardDescription>
                All imported trips from Turo
              </CardDescription>
            </div>
            {hasTrips && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => setClearDialogOpen(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear All Earnings
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : !trips || trips.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No trips imported yet.</p>
                <p className="text-sm mt-1">
                  <Button variant="link" className="p-0 h-auto" onClick={() => setImportDialogOpen(true)}>
                    Import a Turo CSV file
                  </Button>{" "}
                  to get started.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reservation ID</TableHead>
                      <TableHead>Guest</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Trip Start</TableHead>
                      <TableHead>Trip End</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Miles Driven</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total Earnings</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trips.map((trip) => {
                      const highlighted = isTripHighlighted(trip);
                      return (
                      <TableRow
                        key={trip.id}
                        className={highlighted ? "bg-green-500/10 border-l-2 border-l-green-500" : ""}
                      >
                        <TableCell className="font-mono text-sm">
                          {trip.reservation_id || "-"}
                        </TableCell>
                        <TableCell>{trip.guest_name || "-"}</TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {trip.vehicles?.name || trip.turo_vehicle_name || "-"}
                        </TableCell>
                        <TableCell>{formatDate(trip.trip_start)}</TableCell>
                        <TableCell>{formatDate(trip.trip_end)}</TableCell>
                        <TableCell>{trip.trip_days ?? "-"}</TableCell>
                        <TableCell>{trip.distance_traveled ?? "-"}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={
                              trip.trip_status?.toLowerCase() === "completed" 
                                ? "default" 
                                : trip.trip_status?.toLowerCase().includes("cancel")
                                ? "destructive"
                                : "secondary"
                            }
                          >
                            {trip.trip_status || "-"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(trip.total_earnings)}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <EarningsImportDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          hasTrips={hasTrips}
        />

        <ClearTripsDialog
          open={clearDialogOpen}
          onOpenChange={setClearDialogOpen}
          onConfirm={() => {
            analytics.track("Earnings Cleared", {
              trip_count: trips?.length ?? 0,
            });
            clearAllTrips.mutate();
            setClearDialogOpen(false);
          }}
          isLoading={clearAllTrips.isPending}
        />
      </div>
    </AppLayout>
  );
};

export default Earnings;
